import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Recipe, RecipeFormData, RecipeStatus, ConnectorType } from '@/types/database';
import { useToast } from '@/hooks/use-toast';

export function useRecipes(status?: RecipeStatus) {
  return useQuery({
    queryKey: ['recipes', status],
    queryFn: async () => {
      let query = supabase
        .from('recipes')
        .select(`
          *,
          tool_a:tools!recipes_tool_a_id_fkey(*),
          tool_b:tools!recipes_tool_b_id_fkey(*)
        `)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Recipe[];
    },
  });
}

export function usePublishedRecipes() {
  return useQuery({
    queryKey: ['recipes', 'published'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recipes')
        .select(`
          *,
          tool_a:tools!recipes_tool_a_id_fkey(*),
          tool_b:tools!recipes_tool_b_id_fkey(*)
        `)
        .eq('status', 'published')
        .order('is_featured', { ascending: false })
        .order('view_count', { ascending: false });

      if (error) throw error;
      return data as Recipe[];
    },
  });
}

export function useFeaturedRecipes() {
  return useQuery({
    queryKey: ['recipes', 'featured'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recipes')
        .select(`
          *,
          tool_a:tools!recipes_tool_a_id_fkey(*),
          tool_b:tools!recipes_tool_b_id_fkey(*)
        `)
        .eq('status', 'published')
        .eq('is_featured', true)
        .limit(6);

      if (error) throw error;
      return data as Recipe[];
    },
  });
}

export function useRecipe(slug: string) {
  return useQuery({
    queryKey: ['recipes', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recipes')
        .select(`
          *,
          tool_a:tools!recipes_tool_a_id_fkey(*),
          tool_b:tools!recipes_tool_b_id_fkey(*)
        `)
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return data as Recipe | null;
    },
    enabled: !!slug,
  });
}

export function useRecipeById(id: string) {
  return useQuery({
    queryKey: ['recipes', 'id', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recipes')
        .select(`
          *,
          tool_a:tools!recipes_tool_a_id_fkey(*),
          tool_b:tools!recipes_tool_b_id_fkey(*)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data as Recipe | null;
    },
    enabled: !!id,
  });
}

export function useRecipesByTool(toolId: string, toolSlug?: string) {
  return useQuery({
    queryKey: ['recipes', 'tool', toolId, toolSlug],
    queryFn: async () => {
      // Get recipes where tool is sender (tool_a) or receiver (tool_b)
      const { data: directRecipes, error: directError } = await supabase
        .from('recipes')
        .select(`
          *,
          tool_a:tools!recipes_tool_a_id_fkey(*),
          tool_b:tools!recipes_tool_b_id_fkey(*)
        `)
        .eq('status', 'published')
        .or(`tool_a_id.eq.${toolId},tool_b_id.eq.${toolId}`)
        .order('view_count', { ascending: false });

      if (directError) throw directError;
      
      // Check if this tool is a connector platform (make, zapier, n8n)
      // Normalize the slug for matching
      const normalizedSlug = toolSlug?.toLowerCase().replace(/[^a-z0-9]/g, '');
      const connectorMap: Record<string, ('make' | 'zapier' | 'n8n')[]> = {
        'make': ['make'],
        'makecom': ['make'],
        'zapier': ['zapier'],
        'n8n': ['n8n'],
      };
      
      let connectorRecipes: Recipe[] = [];
      const matchedConnectors = normalizedSlug ? connectorMap[normalizedSlug] : null;
      
      if (matchedConnectors) {
        // Get all recipes that use this connector OR have 'both' (for make/zapier)
        const connectorConditions = matchedConnectors.map(c => `connector.eq.${c}`);
        
        // 'both' should match for make and zapier
        if (matchedConnectors.includes('make') || matchedConnectors.includes('zapier')) {
          connectorConditions.push('connector.eq.both');
        }
        
        const { data: connRecipes, error: connError } = await supabase
          .from('recipes')
          .select(`
            *,
            tool_a:tools!recipes_tool_a_id_fkey(*),
            tool_b:tools!recipes_tool_b_id_fkey(*)
          `)
          .eq('status', 'published')
          .or(connectorConditions.join(','))
          .order('view_count', { ascending: false });
        
        if (!connError && connRecipes) {
          connectorRecipes = connRecipes as Recipe[];
        }
      }
      
      // Merge and dedupe results
      const allRecipes = [...(directRecipes as Recipe[])];
      connectorRecipes.forEach(recipe => {
        if (!allRecipes.find(r => r.id === recipe.id)) {
          allRecipes.push(recipe);
        }
      });
      
      return allRecipes;
    },
    enabled: !!toolId,
  });
}

export function useRecipesByToolNames(toolAName: string, toolBName: string) {
  return useQuery({
    queryKey: ['recipes', 'byToolNames', toolAName, toolBName],
    queryFn: async () => {
      // First, find the tools by name
      const { data: toolsData } = await supabase
        .from('tools')
        .select('id, name, slug')
        .or(`name.ilike.%${toolAName}%,name.ilike.%${toolBName}%`);

      if (!toolsData || toolsData.length === 0) return [];

      const toolIds = toolsData.map(t => t.id);
      
      // Get recipes that use any combination of these tools
      const { data, error } = await supabase
        .from('recipes')
        .select('id, title, slug, connector')
        .or(`tool_a_id.in.(${toolIds.join(',')}),tool_b_id.in.(${toolIds.join(',')})`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!(toolAName && toolBName),
  });
}

export function useCreateRecipe() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (recipe: RecipeFormData) => {
      const { data, error } = await supabase
        .from('recipes')
        .insert({
          ...recipe,
          status: recipe.status as RecipeStatus,
          connector: recipe.connector as ConnectorType,
          tool_a_id: recipe.tool_a_id || null,
          tool_b_id: recipe.tool_b_id || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast({
        title: 'Recipe created',
        description: 'The recipe has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error creating recipe',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateRecipe() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...recipe }: RecipeFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('recipes')
        .update({
          ...recipe,
          status: recipe.status as RecipeStatus,
          connector: recipe.connector as ConnectorType,
          tool_a_id: recipe.tool_a_id || null,
          tool_b_id: recipe.tool_b_id || null,
          last_verified_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast({
        title: 'Recipe updated',
        description: 'The recipe has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating recipe',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useQuickUpdateRecipeStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: RecipeStatus }) => {
      const { data, error } = await supabase
        .from('recipes')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      const isPublished = data.status === 'published';
      toast({
        title: isPublished ? 'Recipe published' : 'Recipe unpublished',
        description: isPublished 
          ? 'The recipe is now live.' 
          : 'The recipe has been moved to draft.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating recipe status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('recipes').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast({
        title: 'Recipe deleted',
        description: 'The recipe has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error deleting recipe',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useIncrementViewCount() {
  return useMutation({
    mutationFn: async (id: string) => {
      // Direct update for view count
      const { data: recipe } = await supabase
        .from('recipes')
        .select('view_count')
        .eq('id', id)
        .maybeSingle();
      
      if (recipe) {
        await supabase
          .from('recipes')
          .update({ view_count: (recipe.view_count || 0) + 1 })
          .eq('id', id);
      }
    },
  });
}
